import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Body2 } from "@activecollab/components";
import PropTypes from "prop-types";
import BalanceTooltipContent from "~/components/Subscription/SubscriptionPlanDetails/BalanceTooltipContent";
import PriceTooltipContent from "~/components/Subscription/SubscriptionPlanDetails/PriceTooltipContent";
import StorageUsage from "~/components/Subscription/SubscriptionPlanDetails/StorageUsage";
import SubscriptionInfoIconWithTooltip from "~/components/Subscription/SubscriptionPlanDetails/SubscriptionInfoIconWithTooltip";
import { getPriceBreakdown } from "~/store/selectors/selector.subscriptionBrakeDownPrice";
import { roundAmount } from "~/utils/utils";

const PlanDetails = ({ planInfo, storageUsage, balanceTotal, isTrial }) => {
  const priceBreakDown = useSelector((state) => getPriceBreakdown(state));
  const { t } = useTranslation();

  return (
    <>
      <Body2
        color="primary"
        weight="bold"
        className="tw-mr-8"
        data-testid="planLabel"
      >
        {t("Plan")}
      </Body2>
      <Body2
        color="tertiary"
        data-testid="planValue"
      >
        {isTrial ? t("Free trial of all features") : planInfo.name}
      </Body2>
      <Body2
        color="primary"
        weight="bold"
        className="tw-mr-8"
        data-testid="teamMembersLabel"
      >
        {t("Team Members")}
      </Body2>
      <Body2
        color="tertiary"
        data-testid="teamMembersValue"
      >
        {planInfo.members}
      </Body2>

      <Body2
        color="primary"
        weight="bold"
        className="tw-mr-8"
        data-testid="storageUsageLabel"
      >
        {t("Storage Usage")}
      </Body2>
      <div
        className="tw-flex tw-items-center"
        data-testid="storageUsageValue"
      >
        <StorageUsage
          storage={planInfo.storage}
          storageUsage={storageUsage}
        />
      </div>
      {!isTrial ? (
        <>
          <Body2
            color="primary"
            weight="bold"
            className="tw-mr-8"
            data-testid="priceLabel"
          >
            {t("Price")}
          </Body2>
          <div className="tw-flex tw-items-center">
            {priceBreakDown?.total ? (
              <Body2
                color="tertiary"
                className="tw-flex tw-items-center tw-mr-1"
                data-testid="priceTotalValue"
              >
                ${priceBreakDown.total}
              </Body2>
            ) : null}
            <Body2
              color="tertiary"
              className="tw-flex tw-items-center"
            >
              <SubscriptionInfoIconWithTooltip title={<PriceTooltipContent />} />
            </Body2>
          </div>
        </>
      ) : null}

      {!planInfo.is_suspended && planInfo.is_activated ? (
        <>
          <Body2
            color="primary"
            weight="bold"
            className="tw-mr-8"
            data-testid="nextBillingDateLabelForPlan"
          >
            {planInfo?.nextBillDateLabel}
          </Body2>
          <Body2
            color="tertiary"
            data-testid="nextBillingDateValueForPlan"
          >
            {planInfo?.nextBillDateValue}
          </Body2>
        </>
      ) : null}

      {Number(balanceTotal) > 0 ? (
        <>
          <Body2
            color="primary"
            weight="bold"
            className="tw-mr-8"
            data-testid="balanceLabel"
          >
            {t("Balance")}
          </Body2>
          <div className="tw-flex tw-items-center">
            <Body2
              color="tertiary"
              data-testid="balanceValue"
              className="tw-mr-1"
            >
              ${roundAmount(balanceTotal)}
            </Body2>
            <SubscriptionInfoIconWithTooltip title={<BalanceTooltipContent />} />
          </div>
        </>
      ) : null}
    </>
  );
};

PlanDetails.propTypes = {
  planInfo: PropTypes.object,
  storageUsage: PropTypes.object,
  balanceTotal: PropTypes.number,
  isTrial: PropTypes.bool,
};

export default PlanDetails;
